import React from 'react';
import { FormattedAIResponse } from './FormattedAIResponse';
import { Atom, Search, Brain } from 'lucide-react';
import { getFaviconFromUrl } from '@/utils/favicon';
import { ExternalLink } from 'lucide-react';

interface ParallelSearchResponseProps {
  content: string;
  isStreaming?: boolean;
  onComplete?: () => void;
  isDarkMode: boolean;
  sources?: Array<{ index?: number; url?: string; faviconUrl?: string; title?: string }>;
}

export const ParallelSearchResponse: React.FC<ParallelSearchResponseProps> = ({
  content,
  isStreaming = false,
  onComplete,
  isDarkMode,
  sources = []
}) => {
  // Clean up the content by removing the raw markdown sources section
  // since we display sources in a dedicated component
  const cleanContent = React.useMemo(() => {
    if (!content) return '';
    
    // Remove the "## Sources" section and everything after it
    const sourcesIndex = content.indexOf('## Sources');
    if (sourcesIndex !== -1) {
      return content.substring(0, sourcesIndex).trim();
    }
    
    // Also remove any raw markdown source listings
    const lines = content.split('\n');
    const cleanedLines = [];
    let inSourcesSection = false;
    
    for (const line of lines) {
      if (line.match(/^\d+\.\s*\*\*\[.*\]\(.*\)\*\*$/)) {
        inSourcesSection = true;
        continue;
      }
      if (inSourcesSection && (line.trim() === '' || line.match(/^\s*[>_]/))) {
        continue;
      }
      if (inSourcesSection && !line.match(/^\s*[>_\d]/)) {
        inSourcesSection = false;
      }
      if (!inSourcesSection) {
        cleanedLines.push(line);
      }
    }
    
    return cleanedLines.join('\n').trim();
  }, [content]);

  const getSiteName = (url: string) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./i, '');
      const parts = host.split('.').filter(Boolean);
      if (parts.length <= 1) return host;
      const sld = parts[parts.length - 2];
      const commonSld = new Set(['co', 'com', 'org', 'net', 'gov', 'edu']);
      if (commonSld.has(sld) && parts.length >= 3) return parts[parts.length - 3];
      return sld;
    } catch {
      const safe = url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
      return safe.split('/')[0].split('.')[0] || safe;
    }
  };

  const sourcesList = React.useMemo(() => {
    const urls = (sources || [])
      .map((s: any) => s?.url)
      .filter((u: any): u is string => typeof u === 'string' && !!u);
    const seen = new Set<string>();
    return urls
      .filter((u) => {
        const key = getSiteName(u).toLowerCase();
        if (!key) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((u) => ({ url: u, name: getSiteName(u) }));
  }, [sources]);

  return (
    <div className="space-y-3">
      {/* Research Header */}
      {/* <div className="flex items-center gap-2 mb-3">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-2xl text-xs font-medium ${
          isDarkMode 
            ? 'bg-blue-900/40 text-blue-300 border border-blue-700/70' 
            : 'bg-blue-50 text-blue-700 border border-blue-200/70'
        }`}>
          <Atom className="w-3 h-3" />
          Deep Research
        </div>
      </div> */}
      
      {/* Main Response Content */}
      <div className={`prose prose-sm max-w-none xs:pl-2 sm:pl-3 leading-relaxed ${isDarkMode ? 'prose-invert' : ''}`}>
        <FormattedAIResponse
          content={cleanContent}
          isStreaming={isStreaming}
          onComplete={onComplete}
          isDarkMode={isDarkMode}
        />
      </div>

      {sourcesList.length > 0 && (
        <div className="mt-4">
          <div className={`text-xs font-semibold tracking-widest uppercase opacity-70 ${isDarkMode ? 'text-white/70' : 'text-black/60'}`}>
            Sources
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {sourcesList.map((s, idx) => (
              <a
                key={`${s.url}-${idx}`}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium ${isDarkMode
                  ? 'border-white/10 bg-white/5 text-white hover:bg-white/10'
                  : 'border-black/10 bg-black/5 text-black hover:bg-black/10'
                  }`}
              >
                <span className="max-w-[160px] truncate">{s.name}</span>
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
